/**
 * Parses extractCallDetails API payload and returns auto-selections
 * validated against mergedDetailApiData.products.
 */
export function computeAutoSelectAfterExtract(dataFromApi, mergedDetailApiData) {
  if (!dataFromApi) {
    return { selectedProducts: [], selectedKeyMessagesByProduct: null };
  }

  let parsedProducts = [];
  if (Array.isArray(dataFromApi.Products)) {
    parsedProducts = dataFromApi.Products.map((p) =>
      typeof p === "string" ? p : p?.name || p?.productName || String(p),
    );
  } else if (Array.isArray(dataFromApi.products)) {
    parsedProducts = dataFromApi.products.map((p) =>
      typeof p === "string" ? p : p?.name || String(p),
    );
  } else if (Array.isArray(dataFromApi.product_list)) {
    parsedProducts = dataFromApi.product_list.map((p) =>
      typeof p === "string" ? p : p?.name || String(p),
    );
  }

  const kmsRaw =
    dataFromApi.Key_Messages ??
    dataFromApi.key_messages ??
    dataFromApi.KeyMessages ??
    dataFromApi.keyMessages ??
    null;

  const nextKmMap = {};

  if (Array.isArray(kmsRaw)) {
    kmsRaw.forEach((item) => {
      if (typeof item === "string") {
        if (parsedProducts.length === 1) {
          const p = parsedProducts[0];
          nextKmMap[p] = nextKmMap[p] || [];
          if (!nextKmMap[p].includes(item)) nextKmMap[p].push(item);
        } else {
          nextKmMap.__UNASSIGNED__ = nextKmMap.__UNASSIGNED__ || [];
          if (!nextKmMap.__UNASSIGNED__.includes(item))
            nextKmMap.__UNASSIGNED__.push(item);
        }
      } else if (item && typeof item === "object") {
        const prod =
          item.Product ||
          item.ProductName ||
          item.forProduct ||
          item.product ||
          item.productName ||
          null;
        const kmId =
          item.KeyMessageID ||
          item.KM_ID ||
          item.KeyMessage ||
          item.km ||
          item.id ||
          item.name ||
          null;

        if (prod) {
          nextKmMap[prod] = nextKmMap[prod] || [];
          if (kmId && !nextKmMap[prod].includes(kmId)) {
            nextKmMap[prod].push(kmId);
          }
        } else if (kmId) {
          nextKmMap.__UNASSIGNED__ = nextKmMap.__UNASSIGNED__ || [];
          if (!nextKmMap.__UNASSIGNED__.includes(kmId))
            nextKmMap.__UNASSIGNED__.push(kmId);
        }
      }
    });
  } else if (kmsRaw && typeof kmsRaw === "object") {
    Object.entries(kmsRaw).forEach(([prod, kmsArr]) => {
      if (!Array.isArray(kmsArr)) return;
      nextKmMap[prod] = nextKmMap[prod] || [];
      kmsArr.forEach((km) => {
        if (!nextKmMap[prod].includes(km)) nextKmMap[prod].push(km);
      });
    });
  }

  const flattenedKms = Array.isArray(kmsRaw)
    ? kmsRaw.filter((x) => typeof x === "string").map((s) => s)
    : [];

  if (
    Object.keys(nextKmMap).length === 0 &&
    parsedProducts.length === 1 &&
    flattenedKms.length > 0
  ) {
    nextKmMap[parsedProducts[0]] = Array.from(new Set(flattenedKms));
  }

  const normalize = (s) =>
    String(s || "")
      .toLowerCase()
      .replace(/[^\w\s]/g, "")
      .replace(/\s+/g, " ")
      .trim();

  const availableProducts = (mergedDetailApiData.products || [])
    .map((p) =>
      typeof p === "string" ? p : p?.name || p?.ProductName || "",
    )
    .filter(Boolean);

  const validatedProducts = parsedProducts
    .map((extracted) => {
      const n = normalize(extracted);

      const exact = availableProducts.find((p) => normalize(p) === n);
      if (exact) return exact;

      const partial = availableProducts.find((p) => {
        const np = normalize(p);
        return np.includes(n) || n.includes(np);
      });

      return partial || null;
    })
    .filter(Boolean);

  let selectedKeyMessagesByProduct = null;

  if (validatedProducts.length > 0) {
    const productObjByName = {};
    (mergedDetailApiData.products || []).forEach((p) => {
      const name = typeof p === "string" ? p : p?.name || p?.ProductName;
      if (name) productObjByName[name] = p;
    });

    const validatedKmMap = {};

    const extractedKmTexts = Array.isArray(kmsRaw)
      ? kmsRaw.filter((x) => typeof x === "string")
      : [];

    validatedProducts.forEach((productName) => {
      const productObj = productObjByName[productName];
      if (!productObj) return;

      const kms = Array.isArray(productObj.keyMessages)
        ? productObj.keyMessages
        : [];

      extractedKmTexts.forEach((text) => {
        const q = normalize(text);

        const matched = kms.find((km) => {
          const name = normalize(km?.KeyMessageName || "");
          return name.includes(q) || q.includes(name);
        });

        if (matched) {
          const value = matched?.KeyMessageID || matched?.KeyMessageName;

          validatedKmMap[productName] = validatedKmMap[productName] || [];

          if (!validatedKmMap[productName].includes(value)) {
            validatedKmMap[productName].push(value);
          }
        }
      });
    });

    if (Object.keys(validatedKmMap).length > 0) {
      selectedKeyMessagesByProduct = {};
      Object.entries(validatedKmMap).forEach(([prod, kms]) => {
        selectedKeyMessagesByProduct[prod] = Array.from(new Set(kms));
      });
    }
  }

  return {
    selectedProducts: validatedProducts,
    selectedKeyMessagesByProduct,
  };
}

export function mergeDetailApiFromExtractResult(apiResult, fallbackBuilt) {
  let dataFromApi = null;
  if (
    apiResult &&
    (apiResult.detailingOptions || apiResult.data || apiResult.payload)
  ) {
    dataFromApi =
      apiResult.detailingOptions ||
      apiResult.data ||
      apiResult.payload ||
      apiResult;
  } else {
    dataFromApi = apiResult || null;
  }

  let mergedDetailApiData = fallbackBuilt;
  if (dataFromApi) {
    mergedDetailApiData = {
      ...fallbackBuilt,
      ...dataFromApi,
      products:
        Array.isArray(dataFromApi.products) && dataFromApi.products.length
          ? dataFromApi.products
          : Array.isArray(fallbackBuilt.products)
            ? fallbackBuilt.products
            : [],
    };
  }

  return { dataFromApi, mergedDetailApiData };
}
